// Pure helpers for desktop (browser push) notifications, kept free of
// Firebase and the DOM so they can be unit tested with node --test.

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const PUSH_TOKEN_KEY = 'FirebasePushToken';

// Before opt-in was per user, one flag covered whoever used the browser.
export const LEGACY_OPT_IN_KEY = 'hasEnabledNotifications';

export const optInKey = (userId: string) => `${LEGACY_OPT_IN_KEY}:${userId}`;

const safely = <T>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    return fallback;
  }
};

/**
 * Whether this user turned desktop notifications on in this browser. The
 * legacy browser-wide flag is handed to the first user who signs in after the
 * upgrade (the person who set it), then removed so nobody else inherits it.
 */
export const readOptIn = (storage: KeyValueStorage | undefined, userId?: string | null) => {
  if (!storage || !userId) return false;

  return safely(() => {
    const key = optInKey(userId);
    let value = storage.getItem(key);
    const legacy = storage.getItem(LEGACY_OPT_IN_KEY);

    if (legacy !== null) {
      if (value === null) {
        value = legacy === 'true' ? 'true' : 'false';
        storage.setItem(key, value);
      }
      storage.removeItem(LEGACY_OPT_IN_KEY);
    }

    return value === 'true';
  }, false);
};

export const writeOptIn = (
  storage: KeyValueStorage | undefined,
  userId: string | null | undefined,
  enabled: boolean,
) => {
  if (!storage || !userId) return;
  safely(() => storage.setItem(optInKey(userId), enabled ? 'true' : 'false'), undefined);
};

/** A save only counts when the server answered 2xx with { error: false }. */
export const isSavedResponse = (status: number | undefined, body: unknown) =>
  typeof status === 'number' &&
  status >= 200 &&
  status < 300 &&
  !!body &&
  typeof body === 'object' &&
  (body as { error?: unknown }).error === false;

const SETTINGS_HINT =
  "Check your browser's and your computer's notification settings for this site.";

/** Toast description for a failed test notification, by the server's error code. */
export const testNotificationErrorDescription = (code?: string | null) => {
  switch (code) {
    case 'token-not-registered':
    case 'messaging/registration-token-not-registered':
    case 'messaging/invalid-registration-token':
    case 'messaging/invalid-argument':
      return "This browser's notification registration has expired. Turn desktop notifications off and on again.";
    case 'webchat-unreachable':
    case 'webchat-rejected':
    case 'messaging/server-unavailable':
    case 'messaging/internal-error':
    case 'messaging/quota-exceeded':
      return "The notification service isn't responding. Please try again in a few minutes.";
    case 'messaging/third-party-auth-error':
    case 'messaging/mismatched-credential':
      return "The notification service couldn't send to this browser. Turn desktop notifications off and on again.";
    case 'network-error':
      return 'Check your internet connection and try again.';
    default:
      return `We couldn't send the test notification. ${SETTINGS_HINT}`;
  }
};
